import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DirectEditDomainsComponent } from '@app/modules/direct/components/direct-edit-domains/direct-edit-domains.component';
import { DirectEditInsightsComponent } from '@app/modules/direct/components/direct-edit-insights/direct-edit-insights.component';
import { DirectEditMarketingComponent } from '@app/modules/direct/components/direct-edit-marketing/direct-edit-marketing.component';
import { DirectEditMpsWidgetComponent } from '@app/modules/direct/components/direct-edit-mps-widget/direct-edit-mps-widget.component';
import { DirectEditOverviewComponent } from '@app/modules/direct/components/direct-edit-overview/direct-edit-overview.component';
import { DirectEditPropertiesComponent } from '@app/modules/direct/components/direct-edit-properties/direct-edit-properties.component';
import { DirectPayoutMethodsGetKYCLinkComponent } from '@app/modules/direct/pages/direct-payout-methods-get-kyc-link/direct-payout-methods-get-kyc-link.component';
import { DirectPayoutsMethodsAddTaxInformationComponent } from '@app/modules/direct/pages/direct-payouts-methods-add-tax-information/direct-payouts-methods-add-tax-information';
import { EarningsReportsComponent } from '@app/modules/direct/pages/earnings-reports/earnings-reports.component';
import { AccessToBillingGuard } from '@app/shared/guards/access-to-billing-guard.guard';
import { HasEntitlementForFeatureGuard } from '@app/shared/guards/has-entitlement-for-feature.guard';
import { EntitlementEnum } from '@app/shared/interfaces';
import { FullScreenModalComponent } from '@app/shared/layouts/full-screen-modal/full-screen-modal.component';
import { IsOnboardedToDirectGuard } from './guards/is-onboarded-to-direct.guard';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DirectAddComponent } from './pages/direct-add/direct-add.component';
import { DirectBookingPoliciesComponent } from './pages/direct-booking-policies/direct-booking-policies.component';
import { DirectChannelsListComponent } from './pages/direct-channels-list/direct-channels-list.component';
import { DirectCustomSiteEditComponent } from './pages/direct-custom-site-edit/direct-custom-site-edit.component';
import { DirectEditBookingPolicyComponent } from './pages/direct-edit-booking-policy/direct-edit-booking-policy.component';
import { DirectEditComponent } from './pages/direct-edit/direct-edit.component';
import { DirectInstantBookComponent } from './pages/direct-instant-book/direct-instant-book.component';
import { DirectPayoutHistoryComponent } from './pages/direct-payout-history/direct-payout-history.component';
import { DirectPayoutMethodsComponent } from './pages/direct-payout-methods-list/direct-payout-methods-list.component';
import { DirectPayoutsEditComponent } from './pages/direct-payouts-edit/direct-payouts-edit.component';
import { DirectPayoutsMethodsAddAdyenComponent } from './pages/direct-payouts-methods-add-adyen/direct-payouts-methods-add-adyen.component';
import { DirectPayoutsMethodsAddStripeComponent } from './pages/direct-payouts-methods-add-stripe/direct-payouts-methods-add-stripe';
import { DirectPayoutsStripeOauthSuccessComponent } from './pages/direct-payouts-stripe-oauth-success/direct-payouts-stripe-oauth-success.component';
import { DirectPromoCodesAddComponent } from './pages/direct-promo-codes-add/direct-promo-codes-add.component';
import { DirectPromoCodesEditComponent } from './pages/direct-promo-codes-edit/direct-promo-codes-edit.component';
import { DirectPromoCodesComponent } from './pages/direct-promo-codes/direct-promo-codes.component';
import { DirectSubscriptionComponent } from './pages/direct-subscription/direct-subscription.component';
import { DirectTrialComponent } from './pages/direct-trial/direct-trial.component';
import { DirectWidgetAddComponent } from './pages/direct-widget-add/direct-widget-add.component';

const routes: Routes = [
  {
    path: 'trial',
    component: FullScreenModalComponent,
    data: {
      width: '1280px',
      closeLink: '/',
    },
    children: [
      {
        path: '',
        component: DirectTrialComponent,
        data: {
          title: 'Direct - Start your trial',
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [HasEntitlementForFeatureGuard, IsOnboardedToDirectGuard],
    data: {
      feature: EntitlementEnum.Direct,
      redirectPath: '/demo/direct',
    },
    children: [
      {
        path: '',
        component: DashboardComponent,
        data: {
          title: 'Direct - Dashboard',
        },
      },
      {
        path: 'overview',
        component: DashboardComponent,
        data: {
          title: 'Direct - Dashboard',
        },
      },
      {
        path: 'widgets/new',
        component: DirectWidgetAddComponent,
        data: {
          title: 'Add a new Widget',
        },
      },
      {
        path: 'widgets/:id',
        component: DirectWidgetAddComponent,
        data: {
          title: 'Widget Edit',
        },
      },
      {
        path: 'sites/new',
        component: DirectAddComponent,
        data: {
          title: 'Add a new Site',
        },
      },
      {
        path: 'sites/:siteid',
        component: DirectEditComponent,
        data: {
          title: 'Edit Site',
        },
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            component: DirectEditOverviewComponent,
            data: {
              title: 'Edit Site: Overview',
            },
          },
          {
            path: 'properties',
            component: DirectEditPropertiesComponent,
            data: {
              title: 'Edit Site: Properties',
            },
          },
          {
            path: 'property-search',
            component: DirectEditMpsWidgetComponent,
            data: {
              title: 'Edit Site: Property Search',
              parentType: 'internal',
            },
          },
          {
            path: 'custom-property-search',
            component: DirectEditMpsWidgetComponent,
            data: {
              title: 'Edit Site: Property Search',
            },
          },
          {
            path: 'domains',
            component: DirectEditDomainsComponent,
            data: {
              title: 'Edit Site: Domains',
            },
          },
          {
            path: 'marketing',
            component: DirectEditMarketingComponent,
            data: {
              title: 'Edit Site: Marketing',
            },
          },
          {
            path: 'insights',
            component: DirectEditInsightsComponent,
            data: {
              title: 'Edit Site: Insights',
            },
          },
        ],
      },
      {
        path: 'sites/:siteid',
        redirectTo: '/direct/sites/:siteid/overview',
      },
      {
        path: 'widgets/:id',
        component: DirectWidgetAddComponent,
        data: {
          title: 'Widget Edit',
        },
      },
      {
        path: 'custom-sites/:uuid',
        component: DirectCustomSiteEditComponent,
        data: {
          title: 'Custom Site Edit',
        },
      },
      {
        path: 'payouts',
        component: DirectPayoutMethodsComponent,
        data: {
          title: 'Direct - Payouts Methods',
        },
      },
      {
        path: 'payouts/verify/:uuid',
        component: DirectPayoutMethodsGetKYCLinkComponent,
        data: {
          title: 'Checking...',
        },
      },
      {
        path: 'payouts/new',
        pathMatch: 'full',
        redirectTo: 'payouts/new/stripe-oauth',
      },
      {
        path: 'payouts/new/stripe-oauth',
        component: FullScreenModalComponent,
        data: {
          width: '640px',
          closeLink: '/direct/payouts',
        },
        children: [
          {
            path: '',
            component: DirectPayoutsMethodsAddStripeComponent,
            data: {
              title: 'Direct - Add a Payout Method (Stripe)',
            },
          },
        ],
      },
      {
        path: 'payouts/new/stripe-oauth/success',
        component: FullScreenModalComponent,
        data: {
          width: '640px',
          closeLink: '/direct/payouts',
        },
        children: [
          {
            path: '',
            component: DirectPayoutsStripeOauthSuccessComponent,
            data: {
              title: 'Direct - Payout Success',
            },
          },
        ],
      },
      {
        path: 'payouts/new/adyen',
        component: FullScreenModalComponent,
        data: {
          width: '640px',
          closeLink: '/direct/payouts',
        },
        children: [
          {
            path: '',
            component: DirectPayoutsMethodsAddAdyenComponent,
            data: {
              title: 'Direct - Add a Payout Method',
            },
          },
        ],
      },
      {
        path: 'payouts/edit/:payoutId',
        component: FullScreenModalComponent,
        data: {
          width: '640px',
          closeLink: '/direct/payouts',
        },
        children: [
          {
            path: '',
            component: DirectPayoutsEditComponent,
            data: {
              title: 'Direct - Edit payout method',
            },
          },
        ],
      },
      {
        path: 'payouts/add-tax-information/:payoutId',
        component: FullScreenModalComponent,
        data: {
          width: '640px',
          closeLink: '/direct/payouts',
        },
        children: [
          {
            path: '',
            component: DirectPayoutsMethodsAddTaxInformationComponent,
            data: {
              title: 'Direct - Add tax information',
            },
          },
        ],
      },
      {
        path: 'payout-history',
        component: DirectPayoutHistoryComponent,
        data: {
          title: 'Direct - Payout History',
        },
      },
      {
        path: 'plan',
        component: DirectSubscriptionComponent,
        canActivate: [AccessToBillingGuard],
        data: {
          title: 'Direct - Your Plan',
        },
      },
      {
        path: 'earnings-reports',
        component: EarningsReportsComponent,
        canActivate: [AccessToBillingGuard],
        data: {
          title: 'Earnings Reports',
        },
      },
      {
        path: 'cancellation-policies',
        redirectTo: 'booking-policies',
        pathMatch: 'full',
      },
      {
        path: 'cancellation-policies/new',
        redirectTo: 'booking-policies/new',
        pathMatch: 'full',
      },
      {
        path: 'cancellation-policies/:id',
        redirectTo: 'booking-policies/:id',
        pathMatch: 'full',
      },
      {
        path: 'booking-policies',
        component: DirectBookingPoliciesComponent,
        data: {
          title: 'Direct - Booking Policies',
        },
      },
      {
        path: 'booking-policies/new',
        component: DirectEditBookingPolicyComponent,
        data: {
          title: 'Direct - Edit Booking Policy',
        },
      },
      {
        path: 'booking-policies/:id',
        component: DirectEditBookingPolicyComponent,
        data: {
          title: 'Direct - Edit Booking Policy',
        },
      },
      {
        path: 'channels',
        component: DirectChannelsListComponent,
        data: {
          title: 'Direct - Channels',
        },
      },
      {
        path: 'instant-book',
        component: DirectInstantBookComponent,
        data: {
          title: 'Direct - Instant Book',
        },
      },
      {
        path: 'promotions',
        component: DirectPromoCodesComponent,
        data: {
          title: 'Direct - Promo Codes',
        },
      },
      {
        path: 'promo-codes',
        component: FullScreenModalComponent,
        data: {
          closeLink: '/direct/promotions',
        },
        children: [
          {
            path: 'new',
            component: DirectPromoCodesAddComponent,
            data: {
              title: 'Add a promo code',
            },
          },
        ],
      },
      {
        path: 'promo-codes/:uuid',
        component: DirectPromoCodesEditComponent,
        data: {
          title: 'Edit promo code',
        },
      },
      {
        path: '',
        redirectTo: '/direct/overview',
        pathMatch: 'full',
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DirectRoutingModule {}
